import React from "react";
import { Link } from "react-router-dom";
import { ExternalLink, Music, Heart, Feather } from "lucide-react";
import usePageMeta from "../hooks/usePageMeta";

// Hero image (generated via Nano Banana — swap with a personal image anytime)
const HERO_IMG = "/brand/soar-hero.png";
// Tribute portrait — a bald eagle mid-flight at dawn over misty pine water
const EAGLE_PORTRAIT_IMG = "https://customer-assets-agu9un31.emergentagent.net/job_cozy-gifts-store/artifacts/xsiwlc3u_silhouette_of_a_bald_eagle_white_head_neck_and_upper_body_fe.jpg";
// Shadow & Jackie together — tender FOBBV photo
const NEST_PAIR_IMG = "https://customer-assets-agu9un31.emergentagent.net/job_cozy-gifts-store/artifacts/ijyzgew4_jackieshadowphotoeagleparentsfobbv.png";
// Candle artifact (uploaded to Emergent asset storage)
const CANDLE_IMG = "https://customer-assets-agu9un31.emergentagent.net/job_cozy-gifts-store/artifacts/8buc9mbb_jackiesspiritambercandleetsyil_fullxfull.8370885110_dfwe.webp";

const EAGLE_SONGS = [
  {
    title: "I'll Keep Watch",
    subtitle: "Inspired by Shadow",
    id: "zmm7Kl051z8",
    quote: "Love is never measured\nby how long someone is gone.",
  },
  {
    title: "Two Wings Over Big Bear Valley",
    subtitle: "For the ones who taught us to stay",
    id: "DhTGyyi2qVM",
    quote: "In their care, in their flight —\nthere's a kind of steady light.",
  },
  {
    title: "Two Souls in the Wind",
    subtitle: "Forever Bold · Jackie & Shadow",
    id: "VWI8IhGEhTY",
    quote: "From shadowed nest to open blue,\na bond that time can never lose.",
  },
];

export default function Soar() {
  usePageMeta({
    title: "In Memory of Jackie the Bald Eagle & Dolly Parton — A Tribute | DC Gift World Too",
    description: "A tender tribute to Jackie the Big Bear Valley bald eagle (August 10, 2026) and Dolly Parton (August 25, 2026). Three original songs, a memorial candle benefiting Friends of Big Bear Valley, and Dolly's Imagination Library.",
  });
  return (
    <div className="bg-[var(--bg-cream)]" data-testid="soar-page">
      {/* HERO */}
      <section className="relative overflow-hidden">
        {/* Background image with graceful fallback to gradient */}
        <div className="absolute inset-0">
          <img
            src={HERO_IMG}
            alt=""
            aria-hidden="true"
            className="w-full h-full object-cover"
            onError={(e) => { e.target.style.display = "none"; }}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#1a2820]/85 via-[#2A4A3A]/70 to-[var(--bg-cream)]" />
        </div>

        <div className="relative max-w-4xl mx-auto px-6 py-28 lg:py-40 text-center text-[var(--bg-cream)]">
          <Feather className="w-8 h-8 mx-auto mb-6 text-[var(--gold)]/90" strokeWidth={1.2} />
          <p className="eyebrow text-[var(--gold)] mb-5" data-testid="soar-hero-eyebrow">
            For Jackie · Shadow · Sandy · Luna
          </p>
          <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl tracking-tight leading-[1.05] font-light">
            A spirit
            <span className="block italic text-[var(--gold)] mt-2">that soars</span>
          </h1>
          <p className="mt-10 font-serif italic text-xl sm:text-2xl text-[var(--bg-cream)]/85 max-w-2xl mx-auto leading-relaxed">
            "Love is never measured
            <br />by how long someone is gone."
          </p>
          <p className="mt-4 text-xs uppercase tracking-[0.25em] text-[var(--bg-cream)]/60">
            — I'll Keep Watch
          </p>
        </div>
      </section>

      {/* TRIBUTE PORTRAIT — the eagle in flight */}
      <section className="relative -mt-8 md:-mt-14 z-10">
        <div className="max-w-4xl mx-auto px-6">
          <figure className="rounded-3xl overflow-hidden shadow-2xl border border-[var(--border)] bg-[var(--bg-surface)]">
            <img
              src={EAGLE_PORTRAIT_IMG}
              alt="A bald eagle in flight at dawn — in memory of Jackie"
              className="w-full h-auto block"
              onError={(e) => { e.target.style.display = "none"; }}
              data-testid="soar-eagle-portrait"
            />
            <figcaption className="px-6 py-4 text-center font-serif italic text-sm text-[var(--muted-fg)]">
              A spirit that soars — in memory of Jackie
            </figcaption>
          </figure>
        </div>
      </section>

      {/* STORY */}
      <section className="max-w-3xl mx-auto px-6 py-16 lg:py-24">
        <p className="text-lg text-[var(--ink-forest)] leading-relaxed">
          For years, thousands of us gathered — quietly, from all over the world —
          to watch a bald eagle family in Big Bear Valley. We watched Jackie build
          a nest with Shadow. We held our breath through storms. We celebrated
          when Sandy and Luna hatched and grew their small wings into large ones.
        </p>

        {/* Shadow & Jackie together — nest portrait */}
        <figure className="my-10 rounded-2xl overflow-hidden bg-[var(--bg-surface)] border border-[var(--border)] shadow-sm">
          <img
            src={NEST_PAIR_IMG}
            alt="Shadow and Jackie together at the nest in Big Bear Valley"
            className="w-full h-auto block"
            onError={(e) => { e.target.parentElement.style.display = "none"; }}
            data-testid="soar-nest-pair"
          />
          <figcaption className="px-5 py-3 text-center text-xs uppercase tracking-widest text-[var(--muted-fg)]">
            Shadow &amp; Jackie · Big Bear Valley
            <span className="block mt-1 normal-case tracking-normal text-[11px] text-[var(--muted-fg)]/80 italic">
              Photo courtesy of{" "}
              <a
                href="https://friendsofbigbearvalley.org/"
                target="_blank"
                rel="noopener noreferrer"
                className="underline underline-offset-2 hover:text-[var(--terracotta)]"
              >
                Friends of Big Bear Valley
              </a>
            </span>
          </figcaption>
        </figure>

        <p className="mt-5 text-lg text-[var(--ink-forest)] leading-relaxed">
          Jackie left us on <strong>August&nbsp;10,&nbsp;2026</strong>. But not before teaching us more about
          devotion, patience, and quiet strength than most of us learn in a
          lifetime.
        </p>
        <p className="mt-5 text-lg text-[var(--ink-forest)] leading-relaxed">
          These songs are a small way of saying <em>thank you</em> — to Jackie, to
          Shadow, to the eaglets, and to the community at{" "}
          <a
            href="https://friendsofbigbearvalley.org/livestream/"
            target="_blank"
            rel="noopener noreferrer"
            className="underline decoration-[var(--terracotta)] decoration-2 underline-offset-4 hover:text-[var(--terracotta)]"
            data-testid="soar-fobbv-link"
          >
            Friends of Big Bear Valley
          </a>{" "}
          who protect the habitat that held them all. The livestream is still on —
          Shadow still keeps watch.
        </p>
      </section>

      {/* SONGS */}
      <section className="max-w-6xl mx-auto px-6 pb-20">
        <div className="text-center mb-12">
          <Music className="w-6 h-6 text-[var(--terracotta)] mx-auto mb-3" />
          <p className="eyebrow text-[var(--terracotta)] mb-2">Three songs</p>
          <h2 className="font-serif text-3xl sm:text-4xl text-[var(--ink-forest)] tracking-tight">
            For the family
          </h2>
        </div>
        <div className="grid md:grid-cols-3 gap-8">
          {EAGLE_SONGS.map((s) => (
            <div
              key={s.id}
              className="bg-[var(--bg-surface)] rounded-2xl overflow-hidden border border-[var(--border)] shadow-sm"
              data-testid={`soar-song-${s.id}`}
            >
              <div className="aspect-video bg-black">
                <iframe
                  src={`https://www.youtube.com/embed/${s.id}`}
                  title={s.title}
                  frameBorder="0"
                  allow="accelerometer; encrypted-media; picture-in-picture"
                  allowFullScreen
                  className="w-full h-full"
                />
              </div>
              <div className="p-6">
                <p className="font-serif text-xl text-[var(--ink-forest)] leading-snug">{s.title}</p>
                <p className="mt-1 text-xs uppercase tracking-widest text-[var(--muted-fg)]">
                  {s.subtitle}
                </p>
                <p className="mt-4 font-serif italic text-sm text-[var(--ink-forest)]/80 whitespace-pre-line border-l-2 border-[var(--gold)] pl-3">
                  {s.quote}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* Paws & Harmony credit */}
        <p className="text-center mt-10 text-sm text-[var(--muted-fg)]">
          Songs written &amp; sung with love at{" "}
          <a
            href="https://bit.ly/PawsHarmony"
            target="_blank"
            rel="noopener noreferrer"
            className="text-[var(--terracotta)] underline underline-offset-4 hover:text-[var(--terracotta-deep)]"
            data-testid="soar-paws-link"
          >
            Paws &amp; Harmony
          </a>
        </p>
      </section>

      {/* CANDLE */}
      <section className="bg-[var(--muted)]/40 py-20 lg:py-28">
        <div className="max-w-5xl mx-auto px-6 grid md:grid-cols-2 gap-12 items-center">
          <div className="rounded-3xl bg-[var(--bg-surface)] p-10 flex items-center justify-center">
            <img
              src={CANDLE_IMG}
              alt="Jackie's Spirit — amber jar soy candle"
              className="max-w-xs w-full drop-shadow-xl rounded-lg"
              onError={(e) => { e.target.style.display = "none"; }}
              data-testid="soar-candle-img"
            />
          </div>
          <div>
            <p className="eyebrow text-[var(--terracotta)] mb-3">A candle to remember</p>
            <h2 className="font-serif text-4xl sm:text-5xl text-[var(--ink-forest)] tracking-tight leading-tight">
              Jackie's Spirit
            </h2>
            <p className="mt-5 text-[var(--ink-forest)] leading-relaxed">
              A quiet soy candle — pine, cedar, and a whisper of dawn — for the
              windowsill or the writing desk. Something to light when you miss her.
            </p>
            <p className="mt-4 text-sm text-[var(--ink-forest)]/80 italic flex items-start gap-2">
              <Heart className="w-4 h-4 mt-0.5 text-[var(--terracotta)] shrink-0" />
              <span>
                10% of every candle goes directly to <strong>Friends of Big Bear Valley</strong> to
                protect Moon Camp and the eagles who still call it home.
              </span>
            </p>
            <a
              href="https://www.etsy.com/listing/4554286895/bald-eagle-jackies-spirit-scented-soy"
              target="_blank"
              rel="noopener noreferrer"
              className="mt-8 inline-flex items-center gap-2 bg-[var(--terracotta)] text-white rounded-full px-8 py-4 text-sm font-semibold hover:bg-[var(--terracotta-deep)] transition-colors"
              data-testid="soar-candle-cta"
            >
              Order on Etsy — $23.80 <ExternalLink className="w-4 h-4" />
            </a>
            <p className="mt-4 text-xs text-[var(--muted-fg)]">
              Handmade &amp; shipped from the Etsy studio.
            </p>
          </div>
        </div>
      </section>

      {/* WATCH ALONG */}
      <section className="max-w-3xl mx-auto px-6 py-20 text-center">
        <p className="eyebrow text-[var(--gold)] mb-3">Watch along</p>
        <h2 className="font-serif text-3xl text-[var(--ink-forest)] tracking-tight">
          Shadow still keeps watch
        </h2>
        <p className="mt-5 text-[var(--ink-forest)] leading-relaxed">
          The nest is still there. Shadow is still there. The livestream at Friends
          of Big Bear Valley runs quietly through every season — a small window into
          the wild that Jackie helped protect.
        </p>
        <a
          href="https://friendsofbigbearvalley.org/livestream/"
          target="_blank"
          rel="noopener noreferrer"
          className="mt-7 inline-flex items-center gap-2 border border-[var(--ink-forest)]/25 text-[var(--ink-forest)] rounded-full px-7 py-3 text-sm font-medium hover:bg-[var(--ink-forest)] hover:text-white transition-colors"
          data-testid="soar-livestream-cta"
        >
          Open the livestream <ExternalLink className="w-4 h-4" />
        </a>
      </section>

      {/* DOLLY IN MEMORIAM */}
      <section className="bg-[var(--bg-surface)] border-t border-[var(--border)]">
        <div className="max-w-3xl mx-auto px-6 py-20 lg:py-24 text-center">
          <p className="eyebrow text-[var(--gold)] mb-3">In memoriam</p>
          <h2 className="font-serif text-4xl text-[var(--ink-forest)] tracking-tight">
            Dolly Parton
          </h2>
          <p className="mt-3 text-sm uppercase tracking-[0.25em] text-[var(--muted-fg)]">
            1946 &nbsp;—&nbsp; 2026
          </p>
          <p className="mt-8 text-[var(--ink-forest)] leading-relaxed max-w-xl mx-auto">
            Just two weeks after Jackie, on <strong>August&nbsp;25,&nbsp;2026</strong>, we said goodbye to
            Dolly — who quietly put more than <strong>300 million books</strong> into the hands of
            children through her{" "}
            <a
              href="https://imaginationlibrary.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[var(--terracotta)] underline underline-offset-4"
              data-testid="soar-imagination-link"
            >
              Imagination Library
            </a>
            . She left the light on for all of us.
          </p>

          <div className="mt-10 max-w-2xl mx-auto aspect-video bg-black rounded-2xl overflow-hidden">
            <iframe
              src="https://www.youtube.com/embed/yeJwvV_3r3c"
              title="You Left the Light On — for Dolly"
              frameBorder="0"
              allow="accelerometer; encrypted-media; picture-in-picture"
              allowFullScreen
              className="w-full h-full"
              data-testid="soar-dolly-video"
            />
          </div>
          <p className="mt-4 font-serif italic text-[var(--muted-fg)]">You Left the Light On</p>

          <p className="mt-10 font-serif italic text-lg text-[var(--ink-forest)]/85 max-w-lg mx-auto leading-relaxed border-t border-[var(--border)] pt-8">
            "You left the light on,
            <br />warm beside the door,
            <br />so somebody coming through the darkness
            <br />could find their way once more."
          </p>
        </div>
      </section>

      {/* BACK LINK */}
      <section className="py-16 text-center">
        <Link
          to="/"
          className="text-sm text-[var(--muted-fg)] hover:text-[var(--terracotta)] transition-colors"
          data-testid="soar-back-home"
        >
          ← Back to DC Gift World Too
        </Link>
      </section>
    </div>
  );
}
